import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PLAN_OPTIONS, adminSearchUsers, grantAdminPlan, suspendAdminAccount } from '../services/superadminService';
import { formatDate } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import SearchInput from '../components/ui/SearchInput';
import ConfirmDialog from '../components/ui/ConfirmDialog';

/**
 * Mismo lenguaje que mi_services/subscription.js + activarPlanPorCompra:
 * la app lee SOLO users/{dni}.plan y users/{dni}.subscriptionStatus.
 */
const PLAN_FIELD_MAP = {
  free: { plan: 'free', subscriptionStatus: 'free' },
  trial: { plan: 'trial', subscriptionStatus: 'trial' },
  basic: { plan: 'basic', subscriptionStatus: 'basic' },
  pro: { plan: 'pro', subscriptionStatus: 'premium' },
  premium: { plan: 'premium', subscriptionStatus: 'premium' },
};

function planPreview(plan, expiryDate) {
  const fields = PLAN_FIELD_MAP[plan] || PLAN_FIELD_MAP.free;
  const lines = [
    `plan: '${fields.plan}'`,
    `subscriptionStatus: '${fields.subscriptionStatus}'`,
  ];
  if (plan === 'trial') {
    lines.push('trialStartDate: ISO hoy');
    lines.push('trialEndDate: ISO +7 días');
    lines.push('expiryDate: ISO +7 días');
  } else if (plan === 'free') {
    lines.push('expiryDate: null');
    lines.push('trialStartDate/trialEndDate: null');
  } else if (expiryDate) {
    lines.push(`expiryDate: '${expiryDate}'`);
  } else {
    lines.push('expiryDate: ISO hoy+30 días');
  }
  lines.push("planSource: 'superadmin'");
  return lines.join('\n');
}

export default function SuperadminPlansPage() {
  const { firebaseUser, user } = useAuth();
  // Fuente de verdad: users/{id}.rol === 'superadmin' (Firestore)
  const [claimsChecked, setClaimsChecked] = useState(false);
  const [hasClaim, setHasClaim] = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null);
  const [plan, setPlan] = useState('pro');
  const [expiryDate, setExpiryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('falta_pago');

  // Resolver superadmin: rol en users (principal) o claim (legacy)
  // Acepta 'superAdmin' / 'superadmin' (sin distinguir mayúsculas)
  useEffect(() => {
    let cancelled = false;
    const rol = String(user?.rol || '').toLowerCase();
    if (rol === 'superadmin') {
      setHasClaim(true);
      setClaimsChecked(true);
      return;
    }
    (async () => {
      if (!firebaseUser) {
        setHasClaim(false);
        setClaimsChecked(true);
        return;
      }
      try {
        const token = await firebaseUser.getIdTokenResult();
        if (!cancelled) setHasClaim(token?.claims?.superadmin === true || rol === 'superadmin');
      } catch {
        if (!cancelled) setHasClaim(rol === 'superadmin');
      } finally {
        if (!cancelled) setClaimsChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser, user]);

  const load = useCallback(async () => {
    if (!hasClaim) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setRows(await adminSearchUsers(search));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [hasClaim, search]);

  useEffect(() => {
    if (!claimsChecked) return;
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [claimsChecked, load, search]);

  const onGrant = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await grantAdminPlan({
        email: target.email || undefined,
        dni: target.dni || undefined,
        ruc: target.ruc || undefined,
        plan,
        expiryDate: expiryDate || undefined,
      });
      setTarget(null);
      setExpiryDate('');
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSuspend = async () => {
    if (!suspendTarget) return;
    setBusy(true);
    setError(null);
    try {
      const active = suspendTarget.activo === false;
      await suspendAdminAccount({
        email: suspendTarget.email || undefined,
        dni: suspendTarget.dni || undefined,
        ruc: suspendTarget.ruc || undefined,
        active,
        reason: active ? 'reactivado' : suspendReason || 'falta_pago',
      });
      setSuspendTarget(null);
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!claimsChecked) return <LoadingState label="Verificando permisos…" />;

  if (!hasClaim) {
    return (
      <div className="page">
        <header className="page-header">
          <div>
            <h1>Superadmin · Planes</h1>
            <p className="muted">Asignación manual de planes sin Google Play / App Store</p>
          </div>
        </header>
        <div className="panel">
          <p>
            Tu cuenta <strong>{user?.email}</strong> no tiene <code>rol = superadmin</code>{' '}
            en la colección <code>users</code>.
          </p>
          <p>Actívalo así (una sola vez) desde el repo del core:</p>
          <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, overflow: 'auto' }}>
{`cd /Users/neurum/Desarrollos/MiFacturador-ferreteria
# Buscar tu documento y poner rol: "superadmin"
# users/<tuDni o uid> → { rol: "superadmin", ... }

# O desde un entorno con Admin SDK:
node -e "
const admin = require('./functions/node_modules/firebase-admin');
admin.initializeApp({ projectId: 'mifacturadorya' });
(async () => {
  const db = admin.firestore();
  const email = '${user?.email || 'tu@email.com'}';
  const snap = await db.collection('users').where('email', '==', email).limit(1).get();
  if (snap.empty) throw new Error('user not found');
  await snap.docs[0].ref.update({ rol: 'superadmin' });
  console.log('OK rol=superadmin →', snap.docs[0].id);
})();
"`}
          </pre>
          <p className="muted">
            Luego cierra sesión en este panel y vuelve a entrar para recargar el perfil.
          </p>
        </div>
      </div>
    );
  }

  const selectedPlan = PLAN_OPTIONS.find((p) => p.id === plan);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Superadmin · Planes</h1>
          <p className="muted">
            Escribe <code>users</code> como la app · trial 7 días · suspendido = solo lectura
          </p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por email, DNI, RUC o nombre…"
        />
      </div>

      <div className="panel" style={{ padding: 12 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          <strong>Mapeo idéntico a la app</strong> (<code>getSubscriptionState</code> /{' '}
          <code>activarPlanPorCompra</code>). Solo se escribe <code>users</code>:
          Básico → <code>plan=basic, subscriptionStatus=basic</code> · Pro →{' '}
          <code>plan=pro, subscriptionStatus=premium</code> · Premium →{' '}
          <code>plan=premium, subscriptionStatus=premium</code> · Free →{' '}
          <code>plan=free, subscriptionStatus=free</code>. La app móvil lo lee al
          recargar; no hay desfase panel vs app.
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {loading ? (
        <LoadingState label="Buscando usuarios…" />
      ) : rows.length === 0 ? (
        <EmptyState title="Sin resultados" description="Prueba con el RUC o el email de la empresa." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>RUC</th>
                <th>Plan</th>
                <th>Estado</th>
                <th>Vence</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name} {u.apellidos}
                    <div className="muted">DNI {u.dni || '—'}</div>
                  </td>
                  <td>{u.email || '—'}</td>
                  <td>{u.ruc || '—'}</td>
                  <td>
                    <Badge tone="info">{u.plan || u.subscriptionStatus || '—'}</Badge>
                    {u.planSource === 'superadmin' ? (
                      <Badge tone="ok"> manual</Badge>
                    ) : null}
                  </td>
                  <td>
                    {u.activo ? <Badge tone="ok">Activo</Badge> : <Badge tone="muted">Inactivo</Badge>}
                  </td>
                  <td>{formatDate(u.expiryDate || u.trialEndDate)}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-mini btn-primary"
                        onClick={() => {
                          setTarget(u);
                          setPlan(u.plan || 'pro');
                          setExpiryDate('');
                        }}
                      >
                        Cambiar plan
                      </button>
                      {u.activo === false ? (
                        <button
                          type="button"
                          className="btn btn-mini btn-secondary"
                          onClick={() => {
                            setSuspendTarget(u);
                            setSuspendReason('reactivado');
                          }}
                        >
                          Reactivar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-mini btn-ghost"
                          onClick={() => {
                            setSuspendTarget(u);
                            setSuspendReason('falta_pago');
                          }}
                        >
                          Suspender
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!target}
        title="Asignar plan"
        message={
          <span>
            <strong>{target?.email || target?.id}</strong>
            <br />
            RUC {target?.ruc || '—'}
            <label className="dialog-field">
              Plan
              <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                {PLAN_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedPlan ? (
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: 10,
                  fontSize: '0.82rem',
                  marginTop: 8,
                }}
              >
                <div>
                  Se escribirá en <code>users/{'{id}'}</code>:
                </div>
                <div>
                  <code>
                    plan: "{selectedPlan.plan}" · subscriptionStatus: "{selectedPlan.subscriptionStatus}"
                  </code>
                </div>
                <div className="muted" style={{ marginTop: 6 }}>
                  App: {selectedPlan.access}
                </div>
                {plan === 'pro' ? (
                  <div className="muted">
                    Nota: en el core, Pro usa <code>subscriptionStatus: &quot;premium&quot;</code> (legacy).
                    Compras/PLE siguen siendo solo Premium.
                  </div>
                ) : null}
              </div>
            ) : null}
            {plan !== 'free' && plan !== 'trial' ? (
              <label className="dialog-field">
                Vence (opcional, vacío = 30 días)
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              </label>
            ) : null}
            <div className="dialog-field">
              <span className="muted">Se escribirá en <code>users</code> (mismo idioma que la app):</span>
              <pre
                style={{
                  background: '#0f172a',
                  color: '#e2e8f0',
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 12,
                  margin: '6px 0 0',
                  overflow: 'auto',
                }}
              >
{planPreview(plan, expiryDate)}
              </pre>
            </div>
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
              No se toca <code>suscripciones</code> (solo aplica a compras IAP reales).
              La app móvil leerá este plan al recargar sesión.
            </p>
          </span>
        }
        confirmLabel="Otorgar plan"
        onConfirm={onGrant}
        onCancel={() => setTarget(null)}
        loading={busy}
      />

      <ConfirmDialog
        open={!!suspendTarget}
        title={suspendTarget?.activo === false ? 'Reactivar cuenta' : 'Suspender cuenta'}
        message={
          <span>
            <strong>{suspendTarget?.email || suspendTarget?.id}</strong>
            <br />
            RUC {suspendTarget?.ruc || '—'}
            {suspendTarget?.activo !== false ? (
              <>
                <br />
                La cuenta quedará <strong>inactiva</strong> y sin acceso al panel/app hasta que la reactives.
                <br />
                <span className="muted">
                  Tras suspender: <code>plan=free</code>, <code>subscriptionStatus=suspended</code>.
                  El usuario <strong>no emite</strong> comprobantes (app +{' '}
                  <code>apiPeruProxy</code>). Puede consultar lo ya generado si la app lo permite con sesión
                  válida; el acceso total se corta al volver a entrar.
                </span>
                <label className="dialog-field">
                  Motivo
                  <select value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)}>
                    <option value="falta_pago">Falta de pago</option>
                    <option value="incumplimiento">Incumplimiento de contrato</option>
                    <option value="solicitud_cliente">Solicitud del cliente</option>
                    <option value="otro">Otro</option>
                  </select>
                </label>
              </>
            ) : (
              <p>Se marcará como activa de nuevo.</p>
            )}
          </span>
        }
        confirmLabel={suspendTarget?.activo === false ? 'Reactivar' : 'Suspender'}
        onConfirm={onSuspend}
        onCancel={() => setSuspendTarget(null)}
        loading={busy}
      />
    </div>
  );
}
